/**
 * Maps XTM's internal currency full-name format (e.g. "ALGERIAN_DINAR")
 * to the standard ISO-4217 three-letter currency code (e.g. "DZD").
 *
 * XTM stores currencies using its own human-readable naming convention
 * rather than ISO-4217 codes. This mapping covers all currencies listed
 * in the XTM REST API documentation (v25.6).
 */
export const XTM_CURRENCY_TO_ISO: Record<string, string> = {
  AFGHANI: 'AFN',
  ALBANIAN_LEK: 'ALL',
  ALGERIAN_DINAR: 'DZD',
  ANGOLA_KWANZA: 'AOA',
  ARGENTINE_PESO: 'ARS',
  ARMENIAN_DRAM: 'AMD',
  ARUBAN_FLORIN: 'AWG',
  AUSTRALIAN_DOLLAR: 'AUD',
  AZERBAIJANI_MANAT: 'AZN',
  BAHAMIAN_DOLLAR: 'BSD',
  BAHRAINI_DINAR: 'BHD',
  BANGLADESHI_TAKA: 'BDT',
  BARBADOS_DOLLAR: 'BBD',
  BELARUSIAN_RUBLE: 'BYR',
  BELIZE_DOLLAR: 'BZD',
  BERMUDIAN_DOLLAR: 'BMD',
  BHUTANESE_NGULTRUM: 'BTN',
  BOLIVIANO: 'BOB',
  BOSNIA_CONVERTIBLE_MARK: 'BAM',
  BOTSWANA_PULA: 'BWP',
  BRAZILIAN_REAL: 'BRL',
  BRUNEI_DOLLAR: 'BND',
  BULGARIAN_LEV: 'BGN',
  BURMESE_KYAT: 'MMK',
  BURUNDIAN_FRANC: 'BIF',
  CAMBODIAN_RIEL: 'KHR',
  CANADIAN_DOLLAR: 'CAD',
  CAPE_VERDEAN_ESCUDO: 'CVE',
  CAYMAN_ISLANDS_DOLLAR: 'KYD',
  CFA_FRANC_BCEAO: 'XOF',
  CFA_FRANC_BEAC: 'XAF',
  CFP_FRANC: 'XPF',
  CHILEAN_PESO: 'CLP',
  CHINESE_YUAN: 'CNY',
  COLOMBIAN_PESO: 'COP',
  COMORIAN_FRANC: 'KMF',
  CONGOLESE_FRANC: 'CDF',
  COSTA_RICAN_COLON: 'CRC',
  CROATIAN_KUNA: 'HRK',
  CUBAN_CONVERTIBLE_PESO: 'CUC',
  CUBAN_PESO: 'CUP',
  CZECH_KORUNA: 'CZK',
  DANISH_KRONE: 'DKK',
  DJIBOUTIAN_FRANC: 'DJF',
  DOMINICAN_PESO: 'DOP',
  EAST_CARIBBEAN_DOLLAR: 'XCD',
  EGYPTIAN_POUND: 'EGP',
  ERITREAN_NAKFA: 'ERN',
  ETHIOPIAN_BIRR: 'ETB',
  EURO: 'EUR',
  FALKLAND_ISLANDS_POUND: 'FKP',
  FIJI_DOLLAR: 'FJD',
  GAMBIAN_DALASI: 'GMD',
  GEORGIAN_LARI: 'GEL',
  GHANAIAN_CEDI: 'GHS',
  GIBRALTAR_POUND: 'GIP',
  GUATEMALAN_QUETZAL: 'GTQ',
  GUINEA_FRANC: 'GNF',
  GUYANESE_DOLLAR: 'GYD',
  HAITIAN_GOURDE: 'HTG',
  HONDURAN_LEMPIRA: 'HNL',
  HONG_KONG_DOLLAR: 'HKD',
  HUNGARIAN_FORINT: 'HUF',
  ICELANDIC_KRONA: 'ISK',
  INDIAN_RUPEE: 'INR',
  INDONESIAN_RUPIAH: 'IDR',
  IRANIAN_RIAL: 'IRR',
  IRAQI_DINAR: 'IQD',
  ISRAELI_NEW_SHEKEL: 'ILS',
  // XTM uses NIS as the code in its currency list
  NIS: 'ILS',
  JAMAICAN_DOLLAR: 'JMD',
  JAPANESE_YEN: 'JPY',
  JORDANIAN_DINAR: 'JOD',
  KAZAKHSTANI_TENGE: 'KZT',
  KENYAN_SHILLING: 'KES',
  KUWAITI_DINAR: 'KWD',
  KYRGYZSTANI_SOM: 'KGS',
  LAO_KIP: 'LAK',
  LATVIAN_LATS: 'LVL',
  LEBANESE_POUND: 'LBP',
  LESOTHO_LOTI: 'LSL',
  LIBERIAN_DOLLAR: 'LRD',
  LIBYAN_DINAR: 'LYD',
  LITHUANIAN_LITAS: 'LTL',
  MACANESE_PATACA: 'MOP',
  MACEDONIAN_DENAR: 'MKD',
  MALAGASY_ARIARY: 'MGA',
  MALAWIAN_KWACHA: 'MWK',
  MALAYSIAN_RINGGIT: 'MYR',
  MALDIVIAN_RUFIYAA: 'MVR',
  MAURITANIAN_OUGUIYA: 'MRO',
  MAURITIAN_RUPEE: 'MUR',
  MOLDOVAN_LEU: 'MDL',
  MONGOLIAN_TOGROG: 'MNT',
  MOROCCAN_DIRHAM: 'MAD',
  MOZAMBICAN_METICAL: 'MZN',
  NAMIBIAN_DOLLAR: 'NAD',
  NEPALESE_RUPEE: 'NPR',
  NETHERLANDS_ANTILLEAN_GUILDER: 'ANG',
  NEW_TAIWAN_DOLLAR: 'TWD',
  NEW_ZEALAND_DOLLAR: 'NZD',
  NICARAGUAN_CORDOBA: 'NIO',
  NIGERIAN_NAIRA: 'NGN',
  NORTH_KOREAN_WON: 'KPW',
  NORWEGIAN_KRONE: 'NOK',
  OMANI_RIAL: 'OMR',
  PAKISTANI_RUPEE: 'PKR',
  PANAMANIAN_BALBOA: 'PAB',
  PAPUA_NEW_GUINEAN_KINA: 'PGK',
  PARAGUAYAN_GUARANI: 'PYG',
  PERUVIAN_SOL: 'PEN',
  PHILIPPINE_PESO: 'PHP',
  POLISH_ZLOTY: 'PLN',
  QATARI_RIAL: 'QAR',
  ROMANIAN_LEU: 'RON',
  RUSSIAN_RUBLE: 'RUB',
  RWANDAN_FRANC: 'RWF',
  SAINT_HELENA_POUND: 'SHP',
  SALVADORAN_COLON: 'SVC',
  SAMOAN_TALA: 'WST',
  SAO_TOME_DOBRA: 'STD',
  SAUDI_RIYAL: 'SAR',
  SERBIAN_DINAR: 'RSD',
  SEYCHELLOIS_RUPEE: 'SCR',
  SIERRA_LEONEAN_LEONE: 'SLL',
  SINGAPORE_DOLLAR: 'SGD',
  SOLOMON_ISLANDS_DOLLAR: 'SBD',
  SOMALI_SHILLING: 'SOS',
  SOUTH_AFRICAN_RAND: 'ZAR',
  SOUTH_KOREAN_WON: 'KRW',
  SOUTH_SUDANESE_POUND: 'SSP',
  SRI_LANKAN_RUPEE: 'LKR',
  SUDANESE_POUND: 'SDG',
  SURINAMESE_DOLLAR: 'SRD',
  SWAZI_LILANGENI: 'SZL',
  SWEDISH_KRONA: 'SEK',
  SWISS_FRANC: 'CHF',
  SYRIAN_POUND: 'SYP',
  TAJIKISTANI_SOMONI: 'TJS',
  TANZANIAN_SHILLING: 'TZS',
  THAI_BAHT: 'THB',
  TONGAN_PA_ANGA: 'TOP',
  TRINIDAD_AND_TOBAGO_DOLLAR: 'TTD',
  TUNISIAN_DINAR: 'TND',
  TURKISH_LIRA: 'TRY',
  TURKMENISTAN_MANAT: 'TMT',
  UGANDAN_SHILLING: 'UGX',
  UKRAINIAN_HRYVNIA: 'UAH',
  UNITED_ARAB_EMIRATES_DIRHAM: 'AED',
  US_DOLLAR: 'USD',
  URUGUAYAN_PESO: 'UYU',
  UZBEKISTANI_SOM: 'UZS',
  VANUATU_VATU: 'VUV',
  VENEZUELAN_BOLIVAR: 'VEF',
  VIETNAMESE_DONG: 'VND',
  YEMENI_RIAL: 'YER',
  ZAMBIAN_KWACHA: 'ZMK',
  ZIMBABWE_DOLLAR: 'ZWL',
  POUND_STERLING: 'GBP',
  BRITISH_POUND: 'GBP',
  MEXICAN_PESO: 'MXN',
};

/**
 * Converts an XTM currency identifier to a standard ISO-4217 currency code.
 *
 * If the input is already a valid 2–3 letter ISO code it is returned as-is.
 * If no mapping is found the input is returned unchanged so existing behaviour
 * is preserved rather than silently broken.
 *
 * @param xtmCurrency - The currency value as stored by XTM (e.g. "ALGERIAN_DINAR" or "DZD")
 * @returns ISO-4217 code (e.g. "DZD") or the original value if unmapped
 */
export function toIsoCurrency(xtmCurrency: string | undefined | null): string {
  if (!xtmCurrency) {
    return 'USD';
  }

  // Already a short ISO code (2–3 uppercase letters, no underscore)
  // Short-circuit here so existing 2–3 letter values (e.g. "NIS") are
  // returned verbatim rather than being remapped. This preserves the
  // documented behaviour where callers may explicitly pass through an
  // ISO-like code and expect it to be used unchanged.
  if (/^[A-Z]{2,3}$/.test(xtmCurrency)) {
    return xtmCurrency;
  }

  // Fall back to the XTM full-name → ISO mapping if present.
  const mapped = XTM_CURRENCY_TO_ISO[xtmCurrency];
  if (mapped) {
    return mapped;
  }

  return xtmCurrency;
}
